import { Injectable, TemplateRef, inject } from '@angular/core';
import { DatatableColumn } from '../../datatable/datatable-column';
import { DatatableSort } from '../../datatable/datatable-sort';
import { LookupType } from './lookup-type';
import { PrefixPipe } from '../../utility/prefix.pipe';
import { DatatableStaticTemplateType } from '../../datatable/datatable-static-template';
import { InternalApiUrls } from '../../utility/apiurls-internal';
import { ExternalApiUrls } from '../../utility/apiurls-external';
import { FieldLabelConstants } from 'projects/bis-shared/src/lib/utility/field-label-constants';
import { EnvironmentService } from '../../environment/environment.service';
import { FilterDataType } from '../../datatable/datatable-filter/filter-operations';
import { EntityTypeCode } from '../../utility/system-type-constants';

@Injectable({
  providedIn: 'root',
})
export class GenericLookupService {
  private environmentService = inject(EnvironmentService);
  private internalApiUrls = inject(InternalApiUrls);
  private externalApiUrls = inject(ExternalApiUrls);

  
 

  

  buildHealthcareProviderColumns(idTemplate: TemplateRef<any>): DatatableColumn[] {
    return [
      {
        title: FieldLabelConstants.HealthCareProvider + ' ID',
        property: 'healthcareProviderId',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdFixedTypeCode: EntityTypeCode.HealthcareProvider,
        template: idTemplate,
      },
      { title: 'Provider Name', property: 'name', filterDataType: FilterDataType.String },
      {
        title: 'License #',
        property: 'providerLicenseNumber',
        filterDataType: FilterDataType.String,
      },
      {
        title: FieldLabelConstants.NationalProviderIdentifier,
        property: 'nationalProviderIdentifier',
        filterDataType: FilterDataType.String,
      },
      {
        title: 'Provider Type',
        property: 'providerType',
        filterDataType: FilterDataType.Reference,
      },
      {
        title: 'Address Source',
        property: 'addressSource',
        filterDataType: FilterDataType.Reference,
      },
      { title: 'Address Type', property: 'addressType', filterDataType: FilterDataType.Reference },
      { title: 'Address', property: 'address', filterDataType: FilterDataType.String },
    ];
  }

  buildMedicalSupplierColumns(idTemplate: TemplateRef<any>): DatatableColumn[] {
    return [
      {
        title: 'System ID',
        property: 'medicalSupplierId',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdFixedTypeCode: EntityTypeCode.MedicalSupplier,
        template: idTemplate,
      },
      {
        title: FieldLabelConstants.MedicalSupplierName,
        property: 'medicalSupplierName',
        filterDataType: FilterDataType.String,
      },
      {
        title: FieldLabelConstants.NationalProviderIdentifier,
        property: 'npi',
        filterDataType: FilterDataType.String,
      },
      {
        title: FieldLabelConstants.MedicalSupplierFein,
        property: 'fein',
        filterDataType: FilterDataType.String,
        template: DatatableStaticTemplateType.FeinTemplate,
      },
      {
        title: FieldLabelConstants.MedicalSupplierType,
        property: 'medicalSupplierType',
        filterDataType: FilterDataType.Reference,
      },
    ];
  }

  buildHealthcareProviderSorts(): DatatableSort[] {
    return [
      { property: 'lastName', sortAscending: true },
      { property: 'firstName', sortAscending: true },
      { property: 'providerType', sortAscending: true },
      { property: 'addressSource', sortAscending: true },
      { property: 'addressType', sortAscending: true },
    ];
  }

  buildMedicalSupplierSorts(): DatatableSort[] {
    return [{ property: 'medicalSupplierName', sortAscending: true }];
  }

  buildClaimantColumns(
    idTemplate: TemplateRef<any>,
    ssnTemplate: TemplateRef<any>,
  ): DatatableColumn[] {
    return [
      {
        title: 'Claimant ID',
        property: 'claimantId',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdFixedTypeCode: EntityTypeCode.Claimant,
        template: idTemplate,
      },
      {
        title: FieldLabelConstants.ClaimantFirstName,
        property: 'firstName',
        filterDataType: FilterDataType.String,
      },
      {
        title: FieldLabelConstants.ClaimantMiddleName,
        property: 'middleName',
        filterDataType: FilterDataType.String,
      },
      {
        title: FieldLabelConstants.ClaimantLastName,
        property: 'lastName',
        filterDataType: FilterDataType.String,
      },
      {
        title: 'SSN',
        property: 'socialSecurityNumber',
        filterDataType: FilterDataType.String,
        template: ssnTemplate,
      },
      {
        title: 'Date of Birth',
        property: 'dateOfBirth',
        filterDataType: FilterDataType.Date,
        template: DatatableStaticTemplateType.UtcDateTemplate,
      },
      {
        title: 'Other Identifying Names',
        property: 'otherIdentifyingNames',
        filterDataType: FilterDataType.String,
      },
    ];
  }

  buildClaimantSorts(): DatatableSort[] {
    return [
      { property: 'lastName', sortAscending: true },
      { property: 'firstName', sortAscending: true },
      { property: 'middleName', sortAscending: true },
    ];
  }

  buildInsurerColumns(
    idTemplate: TemplateRef<any>,
    insuranceNumberTemplate: TemplateRef<PrefixPipe>,
  ): DatatableColumn[] {
    return [
      {
        title: 'Insurer ID',
        property: 'insurerId',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdFixedTypeCode: EntityTypeCode.Insurer,
        template: idTemplate,
      },
      { title: 'Insurer Name', property: 'insurerName', filterDataType: FilterDataType.String },
      {
        title: FieldLabelConstants.InsurerFein,
        property: 'fein',
        filterDataType: FilterDataType.String,
        template: DatatableStaticTemplateType.FeinTemplate,
      },
      {
        title: 'Insurer #',
        property: 'insuranceNumber',
        filterDataType: FilterDataType.String,
        template: insuranceNumberTemplate,
        disableSorting: true,
      },
      { title: 'Insurer Type', property: 'insurerType', filterDataType: FilterDataType.Reference },
    ];
  }

  buildInsurerExternalColumns(
    wNumberIdTemplate: TemplateRef<any>,
    bNumberIdTemplate: TemplateRef<any>,
    feinDisplayMaskTemplate: TemplateRef<any>,
  ): DatatableColumn[] {
    return [
      {
        title: 'WC Insurer ID',
        property: 'wNumber',
        filterDataType: FilterDataType.String,
        template: wNumberIdTemplate,
      },
      {
        title: 'DB Insurer ID',
        property: 'bNumber',
        filterDataType: FilterDataType.String,
        template: bNumberIdTemplate,
      },
      { title: 'Insurer Name', property: 'insurerName', filterDataType: FilterDataType.String },
      {
        title: FieldLabelConstants.InsurerFein,
        property: 'fein',
        filterDataType: FilterDataType.String,
        template: feinDisplayMaskTemplate,
      },
      { title: 'Insurer Type', property: 'insurerType', filterDataType: FilterDataType.Reference },
    ];
  }

  buildInsurerSorts(): DatatableSort[] {
    return [
      {
        property: 'insurerName',
        sortAscending: true,
      },
    ];
  }

  buildEmployerColumns(
    idTemplate: TemplateRef<any>,
    addressTemplate: TemplateRef<any>,
  ): DatatableColumn[] {
    return [
      {
        title: 'Employer #',
        property: 'employerId',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdFixedTypeCode: EntityTypeCode.Employer,
        template: idTemplate,
      },
      { title: 'Employer Name', property: 'employerName', filterDataType: FilterDataType.String },
      { title: 'DBA Name', property: 'dbaName', filterDataType: FilterDataType.String },
      {
        title: FieldLabelConstants.EmployerFein,
        property: 'fein',
        filterDataType: FilterDataType.String,
        template: DatatableStaticTemplateType.FeinTemplate,
      },
      {
        title: 'Address',
        property: 'addressLine1',
        filterDataType: FilterDataType.String,
        template: addressTemplate,
        disableSorting: true,
      },
    ];
  }

  buildEmployerSorts(): DatatableSort[] {
    return [
      {
        property: 'employerName',
        sortAscending: true,
      },
      {
        property: 'dbaName',
        sortAscending: true,
      },
      {
        property: 'city',
        sortAscending: true,
      },
    ];
  }

  buildThirdPartyAdministratorColumns(idTemplate: TemplateRef<any>): DatatableColumn[] {
    return [
      {
        title: 'System ID',
        property: 'thirdPartyAdministratorId',
        filterDataType: FilterDataType.SystemId,
        filterSystemIdFixedTypeCode: EntityTypeCode.ThirdPartyAdministrator,
        template: idTemplate,
      },
      {
        title: 'TPA Name',
        property: 'thirdPartyAdministratorName',
        filterDataType: FilterDataType.String,
      },
      {
        title: FieldLabelConstants.TpaFein,
        property: 'fein',
        filterDataType: FilterDataType.String,
        template: DatatableStaticTemplateType.FeinTemplate,
      },
      { title: 'TPA ID', property: 'tpaNumber', filterDataType: FilterDataType.Number },
      {
        title: 'TPA Type',
        property: 'thirdPartyAdministratorType',
        filterDataType: FilterDataType.Reference,
      },
    ];
  }

  buildThirdPartyAdministratorExternalColumns(
    idTemplate: TemplateRef<any>,
    feinDisplayMaskTemplate: TemplateRef<any>,
  ): DatatableColumn[] {
    return [
      {
        title: 'TPA ID',
        property: 'tpaNumber',
        filterDataType: FilterDataType.String,
        template: idTemplate,
      },
      {
        title: 'TPA Name',
        property: 'thirdPartyAdministratorName',
        filterDataType: FilterDataType.String,
      },
      {
        title: 'FEIN',
        property: 'fein',
        filterDataType: FilterDataType.String,
        template: feinDisplayMaskTemplate,
      },
      {
        title: 'TPA Type',
        property: 'thirdPartyAdministratorType',
        filterDataType: FilterDataType.Reference,
      },
    ];
  }

  buildSystemUserColumns(idTemplate: TemplateRef<any>): DatatableColumn[] {
    return [
      {
        title: 'User Name',
        property: 'userName',
        filterDataType: FilterDataType.String,
        template: idTemplate,
      },
      { title: 'First Name', property: 'firstName', filterDataType: FilterDataType.String },
      { title: 'Middle Name', property: 'middleName', filterDataType: FilterDataType.String },
      { title: 'Last Name', property: 'lastName', filterDataType: FilterDataType.String },
      {
        title: 'Email Address',
        property: 'userEmailAddress',
        filterDataType: FilterDataType.String,
      },
    ];
  }

  buildExternalSystemUserColumns(nameTemplate: TemplateRef<any>): DatatableColumn[] {
    return [
      {
        title: 'Name',
        property: 'lastName',
        filterDataType: FilterDataType.String,
        template: nameTemplate,
      },
      { title: 'User Name', property: 'userName', filterDataType: FilterDataType.String },
      {
        title: 'User Role',
        property: 'entityGroupRoleName',
        filterDataType: FilterDataType.String,
      },
      {
        title: 'Email Address',
        property: 'userEmailAddress',
        filterDataType: FilterDataType.String,
      },
      { title: 'Phone #', property: 'phoneNumber', filterDataType: FilterDataType.String },
    ];
  }

  buildThirdPartyAdministratorSorts(): DatatableSort[] {
    return [
      {
        property: 'thirdPartyAdministratorName',
        sortAscending: true,
      },
    ];
  }

  buildExternalSystemUserSorts(): DatatableSort[] {
    return [
      {
        property: 'lastName',
        sortAscending: true,
      },
      {
        property: 'firstName',
        sortAscending: true,
      },
    ];
  }

  buildSystemUserSorts(): DatatableSort[] {
    return [
      {
        property: 'userName',
        sortAscending: true,
      },
    ];
  }

  getDataUrl(lookupType: LookupType): string {
    switch (lookupType) {
      case LookupType.HealthcareProvider:
        return this.internalApiUrls.healthcareProvider.lookup();
      case LookupType.HealthcareProviderSupervisory:
        return this.internalApiUrls.healthcareProviderRelationship.providerSupervisorylookup();
      case LookupType.Claimant:
        return this.internalApiUrls.claimant.search();
      case LookupType.Employer:
        return this.environmentService.isExternalSite
          ? this.externalApiUrls.employer.search()
          : this.internalApiUrls.employer.search();
      case LookupType.Insurer:
        return this.internalApiUrls.insurer.lookup();
      case LookupType.InsurerExternal:
        return this.externalApiUrls.insurer.lookup();
      case LookupType.MedicalSupplier:
        return this.internalApiUrls.medicalSupplier.lookup();
      case LookupType.ThirdPartyAdministrator:
        return this.internalApiUrls.thirdPartyAdministrator.lookup();
      case LookupType.ThirdPartyAdministratorExternal:
        return this.externalApiUrls.thirdPartyAdministrator.lookup();
      case LookupType.ExternalSystemUser:
        return this.environmentService.isExternalSite
          ? this.externalApiUrls.user.getExternalUserLookup()
          : this.internalApiUrls.user.getExternalUserLookup();
      case LookupType.SystemUser:
        return this.internalApiUrls.user.getUserList();
      default:
        throw new Error('Unknown Lookup Type');
    }
  }
}
